import { useId, useState, type ComponentProps, type FormEvent, type ReactNode } from "react";
import {
  Alert,
  ApiError,
  Button,
  FormSkinContext,
  Input,
  RadioGroup,
  apiFetch,
  cn,
  type PortalLoginRequest,
  type PortalLoginResponse,
} from "@dearbill/shared";
import { isoDobOrNull } from "./dob";
import type { PortalLoginFields } from "./loginFields";
import { DateOfBirthField } from "@dearbill/shared/components/DateOfBirthField";
// The one opaque `detail` the backend returns for every login failure (a wrong
// field, an unknown phone, a malformed phone or MBI), so it never reveals which
// field was wrong. Mirrors `_LOGIN_FAIL_DETAIL` in server/app/api/portal.py.
const DETAIL_BAD_VERIFY = "verification details do not match";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";

// This screen opts the shared form primitives into the "enroll" skin through
// FormSkinContext, scoped to this screen only; PortalOtp and the signed-in
// portal keep the default skin.
/** Shared `Input` with the member login's field text size. */
function PortalInput({ className, ...rest }: ComponentProps<typeof Input>): ReactNode {
  return <Input {...rest} className={cn("text-[16px]", className)} />;
}

interface PortalLoginProps {
  /** Field values, owned by the parent so they survive Back/edit. */
  fields: PortalLoginFields;
  onFieldsChange: (fields: PortalLoginFields) => void;
  /**
   * Called with the full login response (the short-lived `challenge` token plus
   * the `bypassSmsOtp` flag the OTP step's banner needs) once the identity
   * match succeeds with a 200 and a non-null `challenge`. This form handles the
   * ambiguous 200s (`mbiRequired`, `identityAmbiguous`, no challenge) itself.
   * The OTP step then obtains the session.
   */
  onChallenge: (res: PortalLoginResponse) => void;
}

interface MethodToggleProps {
  value: PortalLoginFields["method"];
  onChange: (method: PortalLoginFields["method"]) => void;
}

/**
 * Segmented "Personal details | Medicare number" toggle. Semantically it is a
 * radio group, with native arrow-key navigation and checked-state announcements
 * from the visually hidden radios; the labels render as the segments.
 */
function MethodToggle({ value, onChange }: MethodToggleProps): ReactNode {
  // Radios need a shared group name for native keyboard semantics; useId keeps
  // it unique per mounted form.
  const name = useId();
  const options = [
    { value: "identity", label: "Personal details" },
    { value: "mbi", label: "Medicare number" },
  ] as const;
  return (
    <fieldset>
      <legend className="text-midnight mb-1.5 block text-[13px] font-semibold leading-[1.5]">
        Sign in with
      </legend>
      <div className="border-haze divide-haze flex h-[60px] divide-x overflow-hidden rounded-[20px] border-[1.5px]">
        {options.map((opt) => (
          <label
            key={opt.value}
            className={cn(
              "flex flex-1 cursor-pointer items-center justify-center text-center text-[15px] transition-colors",
              "has-[:focus-visible]:ring-ember has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-inset",
              value === opt.value
                ? "text-midnight bg-white font-bold"
                : "text-slate-teal bg-ice hover:text-midnight font-medium",
            )}
          >
            <input
              type="radio"
              name={name}
              value={opt.value}
              checked={value === opt.value}
              onChange={() => onChange(opt.value)}
              className="sr-only"
            />
            {opt.label}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/**
 * Patient-portal identity challenge, step 1 of the OTP-gated login, shown at `/`
 * when there's no session and no challenge. It collects the role (member or
 * caregiver), the phone and one of two identity-factor groups, picked with the
 * segmented toggle (Personal details by default): the MBI, or last name, DOB
 * (mm/dd/yyyy) and ZIP. Only the active group is POSTed to `/api/portal/login`,
 * and the other group's fields are omitted; typed values survive toggling,
 * because the state lives in `Portal`. The MBI, name, DOB and ZIP always
 * describe the patient; `phone` is the member's own or the caregiver's, per
 * `role`. A generic 401 shows opaque copy, and a 429 shows a rate-limit
 * message. Success returns a `challenge`, not a session, and sends no code: the
 * parent renders the OTP step, where the user picks a delivery channel and
 * sends it.
 *
 * Ambiguity guard: when the personal details match several patients with
 * different MBIs, the server answers 200 with `mbiRequired: true` and no
 * `challenge`. The form stays put and reveals a required MBI input below the
 * trio, as an informational notice rather than an error; the re-submit sends
 * the trio and `mbi` so the server can disambiguate. Toggling the sign-in
 * method resets that extra step.
 *
 * Terminal ambiguity: when the factors, including `mbi` when provided, match
 * several patient records the server can't tell apart, the 200 carries
 * `identityAmbiguous: true` instead. There is no challenge, and more factors
 * won't help. The form stays put, withdraws any pending MBI ask and shows a
 * contact-us alert; the alert clears on the next submit or a sign-in-method
 * toggle.
 */
export function PortalLogin({ fields, onFieldsChange, onChallenge }: PortalLoginProps): ReactNode {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // The server asked for the MBI to disambiguate an ambiguous identity match.
  const [mbiRequired, setMbiRequired] = useState(false);
  // Terminal ambiguity: the factors match several records the server can't
  // tell apart, so no re-submit can help.
  const [identityAmbiguous, setIdentityAmbiguous] = useState(false);

  const { role, method, phone, mbi, lastName, dob, zip } = fields;
  const set = (patch: Partial<PortalLoginFields>): void => onFieldsChange({ ...fields, ...patch });
  const isCaregiver = role === "caregiver";
  const mbiLabel = isCaregiver ? "Member's Medicare number (MBI)" : "Medicare number (MBI)";

  const isoDob = isoDobOrNull(dob);
  const factorsComplete =
    method === "mbi"
      ? !!mbi.trim()
      : !!lastName.trim() && isoDob !== null && !!zip.trim() && (!mbiRequired || !!mbi.trim());
  const canSubmit = !busy && !!phone.trim() && factorsComplete;

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    if (!canSubmit) return;
    // Only the active factor group rides in the body. The server treats an
    // omitted field as "not provided", and every provided factor must match.
    let body: PortalLoginRequest;
    if (method === "mbi") {
      body = { role, phone: phone.trim(), mbi: mbi.trim() };
    } else {
      if (isoDob === null) return; // unreachable when canSubmit; narrows the type
      body = { role, phone: phone.trim(), lastName: lastName.trim(), dob: isoDob, zip: zip.trim() };
      // Disambiguation re-submit: every provided factor must match, so the MBI
      // rides alongside the trio, not instead of it.
      if (mbiRequired) body.mbi = mbi.trim();
    }
    setBusy(true);
    setError(null);
    setIdentityAmbiguous(false);
    apiFetch<PortalLoginResponse>(
      "/api/portal/login",
      { method: "POST", body: body as unknown as Record<string, unknown> },
      { baseUrl: API_BASE },
    )
      .then((res) => {
        if (res.identityAmbiguous) {
          // Terminal ambiguity: the submitted factors, even with the MBI,
          // match more than one record and the server can't disambiguate. No
          // challenge was issued and more factors won't help, so withdraw any
          // pending MBI ask and show the contact-us notice instead.
          setBusy(false);
          setMbiRequired(false);
          setIdentityAmbiguous(true);
          return;
        }
        if (res.challenge === null) {
          // Ambiguous match (`mbiRequired`): stay on the form and ask for the
          // MBI as an extra factor; no challenge was issued.
          setBusy(false);
          setMbiRequired(true);
          return;
        }
        onChallenge(res);
      })
      .catch((err: unknown) => {
        setBusy(false);
        if (err instanceof ApiError) {
          if (err.status === 429) {
            setError("Too many attempts. Please try again in about 15 minutes.");
            return;
          }
          if (err.status === 401 && err.detail === DETAIL_BAD_VERIFY) {
            setError("Those details don't match our records — please check them and try again.");
            return;
          }
        }
        setError("Something went wrong. Please try again.");
      });
  };

  return (
    // The enroll-skin opt-in is scoped to the login screen (see the comment
    // above PortalInput); don't lift this provider to the member app root.
    <FormSkinContext.Provider value="enroll">
      <div>
        <h1 className="text-h3 text-midnight font-serif">Check your enrollment status</h1>
        <p className="text-body-sm text-slate-teal mt-2">
          Enter your details below to view the status of your enrollment and upload supporting
          documents.
        </p>
        <form className="mt-8 space-y-5" onSubmit={onSubmit} noValidate>
          <div>
            <RadioGroup
              legend="Who is signing in?"
              options={[
                { value: "member", label: "Member" },
                { value: "caregiver", label: "Caregiver" },
              ]}
              value={role}
              onChange={(e) =>
                set({ role: e.target.value === "caregiver" ? "caregiver" : "member" })
              }
            />
            <p className="text-slate-teal mt-2 text-[13px] leading-[1.5]">
              Choose Caregiver if you&apos;re signing in on behalf of a member using the
              member&apos;s details.
            </p>
          </div>
          <PortalInput
            label={isCaregiver ? "Your phone number" : "Phone number"}
            type="tel"
            required
            autoComplete="tel"
            value={phone}
            onChange={(e) => set({ phone: e.target.value })}
          />
          <MethodToggle
            value={method}
            onChange={(next) => {
              // Switching sign-in method abandons the disambiguation step and
              // any terminal-ambiguity notice.
              setMbiRequired(false);
              setIdentityAmbiguous(false);
              set({ method: next });
            }}
          />
          {method === "mbi" ? (
            <PortalInput
              label={mbiLabel}
              hint="The 11-character number on your red, white & blue Medicare card."
              type="text"
              required
              autoComplete="off"
              autoCapitalize="characters"
              value={mbi}
              onChange={(e) => set({ mbi: e.target.value.toUpperCase() })}
            />
          ) : (
            <>
              <PortalInput
                label={isCaregiver ? "Member's last name" : "Last name"}
                type="text"
                required
                autoComplete="family-name"
                value={lastName}
                onChange={(e) => set({ lastName: e.target.value })}
              />

              <DateOfBirthField
                value={dob}
                onChange={(value) => set({ dob: value })}
                error={undefined}
                required
                autoComplete={{ month: "bday-month", day: "bday-day", year: "bday-year" }}
                labels={{
                  "label.dob": isCaregiver ? "Member's date of birth" : "Date of birth",
                  "dob.month": "Month",
                  "dob.day": "Day",
                  "dob.year": "Year",
                  "dob.autoAdvance": "Typing two digits moves you to the next box automatically.",
                }}
              />
              <PortalInput
                label={isCaregiver ? "Member's ZIP code" : "ZIP code"}
                type="text"
                required
                autoComplete="postal-code"
                inputMode="numeric"
                value={zip}
                onChange={(e) => set({ zip: e.target.value })}
              />
              {mbiRequired && (
                <>
                  <p
                    role="status"
                    className="border-info-border bg-info-bg text-foreground rounded-[20px] border px-4 py-3 text-sm"
                  >
                    We found more than one record matching those details. For your security, please
                    also enter the {isCaregiver ? "member's " : ""}Medicare number (MBI) to
                    continue.
                  </p>
                  <PortalInput
                    label={mbiLabel}
                    type="text"
                    required
                    autoComplete="off"
                    autoCapitalize="characters"
                    value={mbi}
                    onChange={(e) => set({ mbi: e.target.value.toUpperCase() })}
                  />
                </>
              )}
            </>
          )}

          {identityAmbiguous && (
            <Alert
              tone="error"
              title="We found more than one record matching those details and can't verify your identity online. Please contact us for help."
            />
          )}

          {error && <Alert tone="error" title={error} />}

          <Button
            type="submit"
            size="lg"
            className="w-full py-[15px] text-[15px]"
            disabled={!canSubmit}
          >
            {busy ? "Checking…" : "Continue"}
          </Button>
        </form>
      </div>
    </FormSkinContext.Provider>
  );
}
